import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { httpService } from "../../../../services/httpService";
import { checkPermission } from "../../../../lib/utils";
import { useAuth } from "../../../../hooks";
import { API_CONFIG, LOCAL_STORAGE_KEYS, PERMISSION_MAPPER } from "../../../../shared/constants";
import { ICheckOnGoingIntegrationResponse } from "../interface";
import AddIntegration from "./AddIntegration";
import AddIntegrationSkeleton from '../../../../components/SkeletonLoaders/AddIntegrationSkeleton';

const IntegrationResolver = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { user } = useAuth();
    const hasIntegrationCreatePermission = useMemo(() => {
        if (user?.is_owner) return true;
        return checkPermission(
            PERMISSION_MAPPER.INTEGRATION_CREATE,
            "modules",
            user?.user_permissions
        );
    }, [user?.user_permissions, user?.is_owner]);

    const hasIntegrationUpdatePermission = useMemo(() => {
        if (user?.is_owner) return true;
        return checkPermission(
            PERMISSION_MAPPER.INTEGRATION_UPDATE,
            "modules",
            user?.user_permissions
        );
    }, [user?.user_permissions, user?.is_owner]);

    const [resolvedProps, setResolvedProps] = useState<{
        isEditMode: boolean;
        trackingId: string;
        searchParams: URLSearchParams;
        integratingPlatform: 'google_ads' | 'google_admob' | null;
    } | null>(null);

    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const resolveIntegration = async () => {
            setIsLoading(true);
            try {
                // 1. Identify tracking ID
                const savedTrackingId = localStorage.getItem(LOCAL_STORAGE_KEYS.TRACKING_ID);
                const savedMode = localStorage.getItem(LOCAL_STORAGE_KEYS.INTEGRATION_MODE);
                let tid = searchParams.get('trackingId') || savedTrackingId;

                let isEditMode = false;
                let finalTrackingId = '';

                if (tid && tid !== 'add') {
                    // We have an ID, so it's edit mode (unless explicitly marked as 'create' from an ongoing integration)
                    if (savedMode === 'edit' && !hasIntegrationUpdatePermission) {
                        toast.error(t("common.noPermissionUpdate", "You don't have permission to edit integrations."));
                        navigate("/integration-pairs", { replace: true });
                        return;
                    }
                    finalTrackingId = tid;
                    isEditMode = savedMode === 'edit';
                } else {
                    // No ID or 'add', create a new one
                    if (!hasIntegrationCreatePermission) {
                        toast.error(t("common.noPermissionCreate", "You don't have permission to create integrations."));
                        navigate("/integration-pairs", { replace: true });
                        return;
                    }
                    const integration = await httpService.post<ICheckOnGoingIntegrationResponse>(
                        API_CONFIG.path.trackingIntegration
                    );
                    finalTrackingId = integration.tracking_id;
                    isEditMode = false; // Freshly created
                    // Ensure the mode is saved for persistence on refresh
                    localStorage.setItem(LOCAL_STORAGE_KEYS.INTEGRATION_MODE, 'create');
                }

                // 2. Identify platform (needed for callback)
                const savedPlatform = localStorage.getItem(LOCAL_STORAGE_KEYS.INTEGRATING_PLATFORM);

                // 3. Set resolved props
                setResolvedProps({
                    isEditMode,
                    trackingId: finalTrackingId,
                    searchParams,
                    integratingPlatform: (savedPlatform as any) || null
                });

                // 4. Persistence handling
                // We keep items in localStorage so they survive page refresh while on the AddIntegration page.
                // AddIntegration.tsx will handle the final cleanup on finalize/cancel.
                localStorage.setItem(LOCAL_STORAGE_KEYS.TRACKING_ID, finalTrackingId);
                
                navigate(`/integrations?trackingId=${finalTrackingId}`, { replace: true });
            } catch (error) {
                console.error("Failed to resolve integration:", error);
                // Handle error (maybe redirect or show error)
            } finally {
                setIsLoading(false);
            }
        };

        resolveIntegration();
    }, []);

    if (isLoading || !resolvedProps) return <AddIntegrationSkeleton />;

    return (
        <AddIntegration
            isEditMode={resolvedProps.isEditMode}
            trackingId={resolvedProps.trackingId}
            searchParams={resolvedProps.searchParams}
            integratingPlatform={resolvedProps.integratingPlatform}
        />
    );
};

export default IntegrationResolver;